import { cn } from '../../lib/utils';

/**
 * OddsDisplay
 *
 * Shows user's odds of winning based on their ticket count.
 * Displays: "Your odds: 3/50 (6%)"
 */
export default function OddsDisplay({
  userTickets,
  totalTickets,
  showPercentage = true,
  size = 'md',
  className,
}) {
  if (!userTickets || userTickets === 0) {
    return null;
  }

  const percentage = totalTickets > 0
    ? ((userTickets / totalTickets) * 100).toFixed(1)
    : 0;

  const sizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <div className={cn('flex items-center gap-2', sizes[size], className)}>
      <TicketIcon className={cn(
        'text-primary',
        size === 'sm' && 'w-4 h-4',
        size === 'md' && 'w-5 h-5',
        size === 'lg' && 'w-6 h-6'
      )} />
      <span className="text-gray-600">
        Your odds:{' '}
        <span className="font-semibold text-gray-900">
          {userTickets}/{totalTickets}
        </span>
        {showPercentage && (
          <span className="text-primary font-semibold ml-1">
            ({percentage}%)
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * OddsCard
 *
 * Card version showing odds with more detail.
 * Supports both light (cream) and dark backgrounds via the `dark` prop.
 */
export function OddsCard({
  userTickets,
  totalTickets,
  ticketNumbers = [],
  dark = false,
  className,
}) {
  const percentage = totalTickets > 0
    ? ((userTickets / totalTickets) * 100).toFixed(1)
    : 0;

  return (
    <div className={cn(
      'rounded-lg p-4 border',
      dark
        ? 'bg-primary/10 border-primary/30'
        : 'bg-primary/10 border-primary/20',
      className
    )}>
      <div className="flex items-center justify-between mb-3">
        <h4 className={cn('font-medium', dark ? 'text-cream' : 'text-midnight')}>
          Your Entry
        </h4>
        <span className="text-2xl font-mono font-bold text-primary">
          {percentage}%
        </span>
      </div>

      <div className={cn('flex items-baseline gap-1 text-sm', dark ? 'text-cream/60' : 'text-midnight/60')}>
        <span className={cn('font-mono font-semibold', dark ? 'text-cream' : 'text-midnight')}>
          {userTickets}
        </span>
        <span>of</span>
        <span className={cn('font-mono font-semibold', dark ? 'text-cream' : 'text-midnight')}>
          {totalTickets}
        </span>
        <span>tickets</span>
      </div>

      {/* Ticket numbers */}
      {ticketNumbers.length > 0 && (
        <div className="mt-3 pt-3 border-t border-primary/20">
          <p className={cn('text-xs mb-2', dark ? 'text-cream/50' : 'text-midnight/50')}>
            Your ticket numbers:
          </p>
          <div className="flex flex-wrap gap-1">
            {ticketNumbers.map((num) => (
              <span
                key={num}
                className={cn(
                  'inline-flex items-center justify-center w-8 h-8 rounded-md text-sm font-mono font-semibold text-primary border border-primary/20',
                  dark ? 'bg-midnight-lighter' : 'bg-cream'
                )}
              >
                {num}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * OddsComparison
 *
 * Shows odds improvement when adding more tickets.
 */
export function OddsComparison({
  currentTickets,
  newTickets,
  totalTickets,
  className,
}) {
  const currentOdds = totalTickets > 0
    ? ((currentTickets / totalTickets) * 100).toFixed(1)
    : 0;

  const newTotal = totalTickets + newTickets;
  const newOdds = newTotal > 0
    ? (((currentTickets + newTickets) / newTotal) * 100).toFixed(1)
    : 0;

  const improvement = (parseFloat(newOdds) - parseFloat(currentOdds)).toFixed(1);

  return (
    <div className={cn('flex items-center gap-4 text-sm', className)}>
      <div className="text-gray-500">
        Current: <span className="font-semibold text-gray-900">{currentOdds}%</span>
      </div>
      <ArrowIcon className="w-4 h-4 text-gray-400" />
      <div className="text-success-700">
        New: <span className="font-semibold">{newOdds}%</span>
        {parseFloat(improvement) > 0 && (
          <span className="ml-1 text-success">
            (+{improvement}%)
          </span>
        )}
      </div>
    </div>
  );
}

// Icons
function TicketIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
      />
    </svg>
  );
}

function ArrowIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
    </svg>
  );
}
